import * as React from "react";
import { DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import repository from "client/repository";
import Select from "components/form/Select/Select";
import { Callout } from "@octopusdeploy/design-system-components";
import { MoveFileStoragesRequest } from "client/resources/moveFileStoragesRequest";
import { ReefResource } from "client/resources/reefResource";
import { Number } from "shared/Number";
import { reefsRouteLinks } from "areas/reefs/reefsRouteLinks";
import { InternalRedirect } from "components/Navigation/InternalRedirect";
import ExternalLink from "components/Navigation/ExternalLink";

interface Props {
    reef: ReefResource;
}

interface State extends DataBaseComponentState {
    request: Partial<MoveFileStoragesRequest>;
    redirectTo?: string;
}

class MoveFileStoragesDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            request: {
                MaxConcurrentMoveTasks: 1,
            }
        };
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        const request = this.state.request;
        return <SaveDialogLayout title="Move instances between Storage Accounts"
            saveButtonLabel="Move"
            busy={this.state.busy}
            errors={this.state.errors}
            saveButtonDisabled={!request.SourceStorageAccountName || !request.DestinationStorageAccountName || request.SourceStorageAccountName === request.DestinationStorageAccountName}
            onSaveClick={this.moveToNewStorageAccount}>
                <Callout title="Please be aware that..." type="warning">
                    <p>- This will change the availability of the source storage account to <strong>Unavailable</strong></p>
                    <p>- Move operations can cause longer than expected down time for instances. Please make sure you understand the considerations and process before proceeding -
                        see <ExternalLink href="https://github.com/OctopusDeploy/hosted-docs/blob/main/how-to/move-a-cloud-instance-between-reefs.md">How to Move Instances</ExternalLink> for more details.</p>
                    
                </Callout>
                <Select value={request.SourceStorageAccountName}
                        onChange={accountName => this.updateRequest({SourceStorageAccountName: accountName})}
                        items={this.props.reef.StorageAccounts.map(a => ({text: a.Name, value: a.Name}))}
                        fieldName="Source Storage Account Name"
                        hintText="The source storage account to move from"
                />
                <Select value={request.DestinationStorageAccountName}
                        onChange={accountName => this.updateRequest({DestinationStorageAccountName: accountName})}
                        items={this.props.reef.StorageAccounts.filter(a => a.IsAvailable).map(a => ({text: a.Name, value: a.Name}))}
                        fieldName="Destination Storage Account Name"
                        hintText="The destiantion storage account to move to"
                />
                <Number label="Max Concurrent Move Tasks"
                        value={request.MaxConcurrentMoveTasks}
                        min={1}
                        onChange={maxConcurrentMoveTasks => this.updateRequest({MaxConcurrentMoveTasks: maxConcurrentMoveTasks})}
                />
                {request.MaxConcurrentMoveTasks > 1 && <Callout type="danger" title="Are you sure?">
                    <p>Moving more than 1 instance's File Shares at a time can cause the move to break if any of the instances have `Use Azure Backup for moving instance storage` feature flag enabled.
                    See <ExternalLink href="https://github.com/OctopusDeploy/hosted-docs/blob/main/how-to/move-a-cloud-instance-between-reefs.md">How to Move Instances</ExternalLink> for more details.</p>
                </Callout>}
        </SaveDialogLayout>;
    }

    private updateRequest(update: Partial<MoveFileStoragesRequest>) {
        return this.setState(prevState => ({request: {...prevState.request, ...update}}));
    }

    private moveToNewStorageAccount = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            const reefId = this.props.reef.Id;
            await repository.Reefs.moveFileStorages(reefId, this.state.request as MoveFileStoragesRequest);
            this.setState({redirectTo: reefsRouteLinks.reef(reefId).tasks});
        });
    }
}

export default MoveFileStoragesDialog;